import { useEffect, useRef, useState } from "react";

/**
 * A restrained typewriter that cycles example prompts for the composer.
 *
 * The problem this solves is that a blank box does not tell a non-technical
 * person what register to write in — a title? a category? a sentence? — and a
 * single static example only answers that once. Cycling a few shows the range.
 *
 * What makes it safe rather than gimmicky:
 *
 *   - It produces a *string*, and the caller paints it in an `aria-hidden`
 *     overlay. It never touches the textarea's value and never becomes the
 *     field's accessible name, so a screen reader reads the one real, static
 *     `placeholder` once instead of re-announcing every frame.
 *   - `active` is the caller's kill switch. Focus the field or type a single
 *     character and the animation stops mid-word and the overlay is unmounted;
 *     there is no queued timeout that repaints over what someone is writing.
 *   - `prefers-reduced-motion` is honoured by not animating at all: the hook
 *     returns the first example, whole, and never schedules a timer.
 *
 * Timings are deliberately slow. The pause after a finished line is what makes
 * this readable rather than flickery, and deleting runs faster than typing
 * because nobody needs to read a line disappearing.
 */

/*
 * Timings are tuned for someone reading, not for the animation's own sake.
 *
 * Typing at 55ms/char is about the speed of a fast human and reads naturally.
 * Everything else was too quick and the cycle came across as restless: a
 * finished sentence now sits long enough to be read twice, deleting is slower
 * than it was because a fast wipe draws the eye more than the text did, and the
 * gap before the next phrase is a full beat rather than a blink.
 *
 * The opening delay is short on purpose — this is the one pause the user is
 * waiting through rather than reading, and an empty field on arrival should not
 * last.
 */
const TYPE_MS = 55;
const DELETE_MS = 38;
const HOLD_MS = 3800;
const BETWEEN_MS = 1100;
const START_MS = 350;

export interface TypedExamplesOptions {
  /** The phrases to cycle. The first is also the reduced-motion fallback. */
  examples: readonly string[];
  /** False while the field is focused or holds a draft — stops and resets. */
  active: boolean;
}

export function useTypedExamples({ examples, active }: TypedExamplesOptions): string {
  const [text, setText] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (!active || examples.length === 0) {
      return;
    }

    // Start from nothing every time it re-activates. Without this, clearing a
    // half-written message shows the tail of whatever phrase was mid-type when
    // the user first started typing — `text` survives the deactivation, and the
    // first new frame is 400ms away.
    setText("");

    // Read the preference at effect time rather than through a subscription:
    // someone flipping it mid-session gets the new behaviour on the next mount,
    // and the common case costs nothing.
    const reduced =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduced) {
      setText(examples[0] ?? "");
      return;
    }

    let index = 0;
    let length = 0;
    let deleting = false;
    let cancelled = false;

    const step = () => {
      if (cancelled) {
        return;
      }
      const phrase = examples[index] ?? "";

      if (!deleting) {
        length += 1;
        setText(phrase.slice(0, length));
        if (length >= phrase.length) {
          deleting = true;
          timer.current = setTimeout(step, HOLD_MS);
          return;
        }
        timer.current = setTimeout(step, TYPE_MS);
        return;
      }

      length -= 1;
      setText(phrase.slice(0, Math.max(length, 0)));
      if (length <= 0) {
        deleting = false;
        index = (index + 1) % examples.length;
        timer.current = setTimeout(step, BETWEEN_MS);
        return;
      }
      timer.current = setTimeout(step, DELETE_MS);
    };

    timer.current = setTimeout(step, START_MS);

    return () => {
      cancelled = true;
      if (timer.current !== null) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, [active, examples]);

  return active ? text : "";
}
